import { useEffect, useRef } from 'react'
import { useMedplum } from '@medplum/react'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { hasLdClientId, markLdIdentified } from '@/hooks/useLdIdentified'
import { logWarning } from '@/utils/errorUtils'

export function LDUserIdentifier() {
  const medplum = useMedplum()
  const ldClient = useLDClient()
  const identifiedRef = useRef<string | null>(null)

  const profile = medplum.getProfile()
  const profileId = profile?.id

  useEffect(() => {
    if (!hasLdClientId || !ldClient || !profileId || identifiedRef.current === profileId) {
      return
    }

    identifiedRef.current = profileId

    // Per-user flags (e.g. populationHealth) wait for this; a failed identify still settles it,
    // so they fall back to the anonymous values instead of waiting forever. Only the current
    // profile's identify settles it: after an account switch, the previous user's identify can
    // finish last and must not mark the old key identified.
    const settle = () => {
      if (identifiedRef.current === profileId) markLdIdentified(profileId)
    }
    ldClient
      .identify({
        kind: 'user',
        key: profileId,
        name: profile?.name?.[0]
          ? `${profile.name[0].given?.[0] || ''} ${profile.name[0].family || ''}`.trim()
          : undefined,
      })
      .then(settle, (err: unknown) => {
        logWarning('launchdarkly', 'identify', String(err))
        settle()
      })
  }, [ldClient, profileId, profile])

  return null
}
